/**
 * Broadcast metadata fan-out over the SFU's DataChannels API.
 *
 * The SFU does not open a channel for you: it assigns an SCTP stream id and
 * both sides bind a `negotiated: true` RTCDataChannel to it. `ondatachannel`
 * never fires, which is why an implementation that only called
 * `createDataChannel` on each peer looked correct and delivered nothing.
 *
 * A listener session that has only pulled tracks has no SCTP transport at all,
 * so it must establish one — a renegotiation — before subscribing.
 */

/** Name the metadata channel is published under. Must match the Worker. */
export const METADATA_CHANNEL = "metadata";

export interface DataChannelNegotiation {
  id: number;
  requiresImmediateRenegotiation?: boolean;
  sessionDescription?: { sdp: string; type: RTCSdpType };
}

export interface TransportNegotiation {
  requiresImmediateRenegotiation?: boolean;
  sessionDescription?: { sdp: string; type: RTCSdpType };
}

export interface MetadataChannelApi {
  /** Studio: publish the channel, returning its SCTP stream id. */
  publishMetadataChannel(sessionId: string): Promise<DataChannelNegotiation>;
  /** Tuner: give a pull-only session an SCTP transport. */
  establishDataChannelTransport(sessionId: string): Promise<TransportNegotiation>;
  /** Tuner: subscribe to the live Broadcast's metadata channel. */
  subscribeMetadataChannel(sessionId: string): Promise<DataChannelNegotiation>;
  renegotiateSession(sessionId: string, sdpAnswer: string): Promise<unknown>;
}

/** Minimal client surface, satisfied by `@riq/api`'s ApiClient. */
export interface MetadataChannelApiClient {
  publishMetadataChannel(sessionId: string): Promise<Record<string, unknown>>;
  establishDataChannelTransport(sessionId: string): Promise<Record<string, unknown>>;
  subscribeMetadataChannel(sessionId: string): Promise<Record<string, unknown>>;
  renegotiateSession(sessionId: string, sdpAnswer: string): Promise<Record<string, unknown>>;
}

function toNegotiation(data: Record<string, unknown>): DataChannelNegotiation {
  return {
    id: data.id as number,
    requiresImmediateRenegotiation: data.requiresImmediateRenegotiation as boolean | undefined,
    sessionDescription: data.sessionDescription as { sdp: string; type: RTCSdpType } | undefined,
  };
}

/** Adapt an `@riq/api` client to the typed {@link MetadataChannelApi}. */
export function createMetadataChannelApi(client: MetadataChannelApiClient): MetadataChannelApi {
  return {
    publishMetadataChannel: (sessionId) =>
      client.publishMetadataChannel(sessionId).then(toNegotiation),
    establishDataChannelTransport: (sessionId) =>
      client.establishDataChannelTransport(sessionId).then((data) => ({
        requiresImmediateRenegotiation: data.requiresImmediateRenegotiation as boolean | undefined,
        sessionDescription: data.sessionDescription as
          | { sdp: string; type: RTCSdpType }
          | undefined,
      })),
    subscribeMetadataChannel: (sessionId) =>
      client.subscribeMetadataChannel(sessionId).then(toNegotiation),
    renegotiateSession: (sessionId, sdpAnswer) => client.renegotiateSession(sessionId, sdpAnswer),
  };
}

/** Apply an SFU-initiated renegotiation, if the response asked for one. */
async function applyRenegotiation(
  peerConnection: RTCPeerConnection,
  api: Pick<MetadataChannelApi, "renegotiateSession">,
  sessionId: string,
  response: TransportNegotiation,
): Promise<void> {
  if (!response.requiresImmediateRenegotiation || !response.sessionDescription) return;

  await peerConnection.setRemoteDescription(response.sessionDescription);
  const answer = await peerConnection.createAnswer();
  await peerConnection.setLocalDescription(answer);
  await api.renegotiateSession(sessionId, answer.sdp!);
}

function bindNegotiatedChannel(
  peerConnection: RTCPeerConnection,
  streamId: number,
): RTCDataChannel {
  return peerConnection.createDataChannel(METADATA_CHANNEL, {
    negotiated: true,
    id: streamId,
    ordered: true,
  });
}

/**
 * Studio side: publish the metadata channel and return a channel to send on.
 * The PeerConnection must already have an SCTP transport, which it does because
 * ingest creates a bootstrap data channel before its offer.
 */
export async function publishMetadataChannel(
  peerConnection: RTCPeerConnection,
  api: Pick<MetadataChannelApi, "publishMetadataChannel" | "renegotiateSession">,
  sessionId: string,
): Promise<RTCDataChannel> {
  const negotiation = await api.publishMetadataChannel(sessionId);
  await applyRenegotiation(peerConnection, api, sessionId, negotiation);
  return bindNegotiatedChannel(peerConnection, negotiation.id);
}

/**
 * Tuner side: establish a transport, subscribe, and return the channel that
 * carries the Operator's metadata.
 *
 * Call this only after audio is playing — it costs two round trips and a
 * renegotiation, and metadata is not worth delaying sound for.
 */
export async function subscribeMetadataChannel(
  peerConnection: RTCPeerConnection,
  api: Pick<
    MetadataChannelApi,
    "establishDataChannelTransport" | "subscribeMetadataChannel" | "renegotiateSession"
  >,
  sessionId: string,
): Promise<RTCDataChannel> {
  const transport = await api.establishDataChannelTransport(sessionId);
  await applyRenegotiation(peerConnection, api, sessionId, transport);

  const negotiation = await api.subscribeMetadataChannel(sessionId);
  await applyRenegotiation(peerConnection, api, sessionId, negotiation);

  return bindNegotiatedChannel(peerConnection, negotiation.id);
}
